import React, { memo, useCallback, useState, useEffect } from 'react';
import { Row, Col, Form } from 'react-bootstrap';
import { formatInTimeZone } from 'date-fns-tz';
import {
  ScoreSheetLineup,
  ScoreSheetJudgeLineup,
  ScoreSheetAA,
} from '../shared/lineups';
import { ScoreSheetRounds } from '../shared/rounds';
import { formatAttendanceValue, coachData } from '../shared/utils';
import { convertMillipointsToDisplay } from '../../../../utilities/scoring';
import { Clock } from '../shared/clock';
import { useJudgeAssignment } from '../shared/useJudgeAssignment';
import JudgeSearchInput from '../shared/JudgeSearchInput';
import styles from '../../officialscoresheet.module.css';

const {
  header,
  row,
  teamName,
  teamNameLabel,
  teamNameTeam,
  matchDetailsShort,
  matchDetailsLong,
  matchDetailsLabel,
  matchDetailsShortField,
  matchDetailsLongField,
  matchDetailsEditableField,
  editableField,
  editablePlaceholder,
  body,
  squadLeft,
  squadRight,
  squadFull,
  womensSingleSheet,
  footer,
  subFooter,
  signature,
  signatureLabel,
  signatureField,
  signatureName,
  signatureNames,
  timestamp,
  teamScore,
  teamScoreLabel,
  teamScoreValue,
  teamScoreValueEmpty,
  rotationScore,
  yellowCard,
  apparatusRow,
  flexTableWrapper,
} = styles;

function WomensSingleHeader({
  team,
  format,
  date,
  timezone,
  location,
  attendance,
  session,
  editable,
  onLocationChange,
  onAttendanceChange,
}) {
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isEditingAttendance, setIsEditingAttendance] = useState(false);
  const [localLocation, setLocalLocation] = useState(location);
  const [localAttendance, setLocalAttendance] = useState(attendance);

  const handleLocationClick = () => {
    if (editable) {
      setLocalLocation(location);
      setIsEditingLocation(true);
    }
  };

  const handleAttendanceClick = () => {
    if (editable) {
      setLocalAttendance(attendance);
      setIsEditingAttendance(true);
    }
  };

  const handleLocationBlur = () => {
    setIsEditingLocation(false);
    if (localLocation !== location) {
      onLocationChange?.(localLocation);
    }
  };

  const handleAttendanceBlur = () => {
    setIsEditingAttendance(false);
    if (localAttendance !== attendance) {
      onAttendanceChange?.(localAttendance);
    }
  };

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditingLocation(false);
      if (localLocation !== location) {
        onLocationChange?.(localLocation);
      }
    } else if (e.key === 'Escape') {
      setIsEditingLocation(false);
      setLocalLocation(location);
    }
  };

  const handleAttendanceKeyDown = (e) => {
    if (e.key === 'Enter') {
      setIsEditingAttendance(false);
      if (localAttendance !== attendance) {
        onAttendanceChange?.(localAttendance);
      }
    } else if (e.key === 'Escape') {
      setIsEditingAttendance(false);
      setLocalAttendance(attendance);
    }
  };

  return (
    <section className={header}>
      <h1>{format.title}</h1>
      <Row className={row}>
        <Col className={teamName}>
          <div className={teamNameLabel}>Session: </div>
          <div className={teamNameTeam}>{session?.name || ''}</div>
        </Col>
        <Col className={teamName}>
          <div className={teamNameLabel}>Team: </div>
          <div className={teamNameTeam}>
            {(team && team?.forceTitle
              ? `${team?.altName}: ${team?.displayName}`
              : team?.altName) || ''}
            {team?.individuals ? ' (Individuals)' : ''}
          </div>
        </Col>
      </Row>
      <Row className={row}>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Date: </div>
          <div className={matchDetailsShortField}>{`${formatInTimeZone(
            new Date(date),
            timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            "M'/'dd'/'yyyy"
          )}`}</div>
        </Col>
        <Col className={matchDetailsLong}>
          <div className={matchDetailsLabel}>Location: </div>
          {isEditingLocation ? (
            <input
              type="text"
              className={matchDetailsEditableField}
              value={localLocation}
              onChange={(e) => setLocalLocation(e.target.value)}
              onBlur={handleLocationBlur}
              onKeyDown={handleLocationKeyDown}
              autoFocus
              placeholder="Enter location..."
            />
          ) : (
            <div
              className={`${matchDetailsLongField} ${
                editable ? editableField : ''
              }`}
              onClick={handleLocationClick}
              title={editable ? 'Click to edit' : undefined}
            >
              {location ||
                (editable ? (
                  <span className={editablePlaceholder}>+ Add</span>
                ) : (
                  ''
                ))}
            </div>
          )}
        </Col>
        <Col className={matchDetailsShort}>
          <div className={matchDetailsLabel}>Attendance: </div>
          {isEditingAttendance ? (
            <input
              type="text"
              className={matchDetailsEditableField}
              value={localAttendance}
              onChange={(e) => setLocalAttendance(e.target.value)}
              onBlur={handleAttendanceBlur}
              onKeyDown={handleAttendanceKeyDown}
              autoFocus
              placeholder="Enter attendance..."
            />
          ) : (
            <div
              className={`${matchDetailsShortField} ${
                editable ? editableField : ''
              }`}
              onClick={handleAttendanceClick}
              title={editable ? 'Click to edit' : undefined}
            >
              {formatAttendanceValue(attendance) ||
                (editable ? (
                  <span className={editablePlaceholder}>+ Add</span>
                ) : (
                  ''
                ))}
            </div>
          )}
        </Col>
      </Row>
    </section>
  );
}

function WomensSingleBody({
  team,
  apparatusArray,
  lineupMax,
  teamsByAthleteId,
  judgesByApparatus,
  gender,
  session,
  editable,
  onRefetch,
}) {
  // Use the judge assignment hook for editable mode
  const { assignJudge, removeJudge, bulkAssignJudges } = useJudgeAssignment(
    session,
    onRefetch,
    gender
  );

  // State for bulk assignment progress
  const [bulkProgress, setBulkProgress] = useState(null);
  const [canCloseModal, setCanCloseModal] = useState(false);
  const [autoAssignCountdown, setAutoAssignCountdown] = useState(null);

  // Auto-dismiss modal after completion with dwell time
  useEffect(() => {
    if (bulkProgress?.phase === 'done') {
      setCanCloseModal(false);
      // Wait 3 seconds before allowing close (gives time to see results + refetch)
      const timer = setTimeout(() => {
        setCanCloseModal(true);
      }, 3000);
      return () => clearTimeout(timer);
    } else {
      setCanCloseModal(false);
    }
  }, [bulkProgress?.phase]);

  // Auto-proceed countdown when all judges are exact matches
  useEffect(() => {
    if (bulkProgress?.phase === 'needs_resolution') {
      const hasIssues = bulkProgress.assignments?.some(
        (a) =>
          a.status === 'not_found' || (a.status === 'found' && !a.isExactMatch)
      );

      if (!hasIssues) {
        // All exact matches - start 3s countdown
        setAutoAssignCountdown(3);
        const interval = setInterval(() => {
          setAutoAssignCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return () => clearInterval(interval);
      } else {
        setAutoAssignCountdown(null);
      }
    } else {
      setAutoAssignCountdown(null);
    }
  }, [bulkProgress?.phase, bulkProgress?.assignments]);

  const handleJudgeAssign = useCallback(
    async (judgeIndex, judge, scoreType, apparatus) => {
      try {
        await assignJudge(judge, scoreType, apparatus);
      } catch (err) {
        console.error('Failed to assign judge:', err);
      }
    },
    [assignJudge]
  );

  const handleJudgeRemove = useCallback(
    async (judgeIndex, sessionJudgeAssignmentId, _version) => {
      try {
        await removeJudge(sessionJudgeAssignmentId, _version);
      } catch (err) {
        console.error('Failed to remove judge:', err);
      }
    },
    [removeJudge]
  );

  // Auto-trigger assignment when countdown reaches 0
  const handleContinueRef = React.useRef(null);
  useEffect(() => {
    if (autoAssignCountdown === 0 && handleContinueRef.current) {
      handleContinueRef.current();
    }
  }, [autoAssignCountdown]);

  // Store parsed assignments for continuation after resolution
  const [parsedAssignments, setParsedAssignments] = useState(null);
  const [judgeOverrides, setJudgeOverrides] = useState({});

  const handleBulkJudgeAssign = useCallback(
    async (assignments, currentApparatus) => {
      try {
        setParsedAssignments(assignments);
        setJudgeOverrides({});

        // Search only - always pause to let user review before proceeding
        await bulkAssignJudges(
          assignments,
          (progress) => setBulkProgress(progress),
          { searchOnly: true }
        );

        // Always pause after search so user can review matches before continuing
        // User will click "Continue" to proceed with assignment
      } catch (err) {
        console.error('Failed to bulk assign judges:', err);
        setBulkProgress(null);
      }
    },
    [bulkAssignJudges]
  );

  // Continue assignment after user resolves not_found judges
  const handleContinueAssignment = useCallback(async () => {
    if (!parsedAssignments) return;
    setAutoAssignCountdown(null); // Stop countdown if manually triggered

    try {
      const results = await bulkAssignJudges(
        parsedAssignments,
        (progress) => setBulkProgress(progress),
        { dryRun: false, judgeOverrides }
      );

      if (results.failed.length > 0) {
        console.warn(
          'Some judges could not be assigned:',
          results.failed.map((f) => `${f.name}: ${f.reason}`).join(', ')
        );
      }
    } catch (err) {
      console.error('Failed to bulk assign judges:', err);
      setBulkProgress(null);
    }
  }, [parsedAssignments, judgeOverrides, bulkAssignJudges]);

  // Keep ref updated for auto-trigger
  handleContinueRef.current = handleContinueAssignment;

  // Handle user selecting an alternative judge for a not_found entry
  const handleSelectAlternative = useCallback((originalName, selectedJudge) => {
    setAutoAssignCountdown(null); // Cancel auto-assign on intervention
    setJudgeOverrides((prev) => ({
      ...prev,
      [originalName]: selectedJudge,
    }));
    // Update the bulkProgress to reflect the selection
    setBulkProgress((prev) => {
      if (!prev?.assignments) return prev;
      return {
        ...prev,
        assignments: prev.assignments.map((a) =>
          a.name === originalName
            ? {
                ...a,
                status: 'found',
                matchedName: selectedJudge.name,
                isExactMatch: false,
              }
            : a
        ),
      };
    });
  }, []);

  const lineupMaxes = apparatusArray.map((a, i) => {
    if (team?.individuals) {
      return Math.max(
        team?.events?.[i]?.lineup?.length || lineupMax,
        lineupMax
      );
    }
    return lineupMax;
  });

  return (
    <main className={`${body} ${womensSingleSheet}`}>
      {/* Bulk assignment progress overlay */}
      {bulkProgress && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.4)',
              zIndex: 9998,
            }}
            onClick={() => {
              // Don't dismiss during resolution or during dwell period after completion
              const isDwelling =
                bulkProgress.phase === 'done' && !canCloseModal;
              if (bulkProgress.phase !== 'needs_resolution' && !isDwelling) {
                setBulkProgress(null);
              }
            }}
          />
          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#fff',
              color: '#333',
              borderRadius: '12px',
              zIndex: 9999,
              width: '520px',
              maxWidth: '90vw',
              maxHeight: '85vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: '20px 24px 16px',
                borderBottom: '1px solid #e5e5e5',
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: '4px',
                }}
              >
                Bulk Judge Assignment
              </div>
              <div
                style={{
                  fontSize: '13px',
                  color: '#888',
                }}
              >
                {bulkProgress.phase === 'search' && 'Searching for judges...'}
                {bulkProgress.phase === 'cleanup' &&
                  'Cleaning up old assignments...'}
                {bulkProgress.phase === 'assign' &&
                  'Assigning judges to events...'}
                {bulkProgress.phase === 'needs_resolution' &&
                  (bulkProgress.assignments?.some(
                    (a) =>
                      a.status === 'not_found' ||
                      (a.status === 'found' && !a.isExactMatch)
                  )
                    ? 'Review matches below. You can search for alternatives or click Continue.'
                    : autoAssignCountdown !== null
                    ? `All judges found. Assigning in ${autoAssignCountdown}s...`
                    : 'All judges found. Click Continue to assign them.')}
                {bulkProgress.phase === 'done' &&
                  !canCloseModal &&
                  'Refreshing scoresheet...'}
                {bulkProgress.phase === 'done' &&
                  canCloseModal &&
                  `${
                    bulkProgress.assignments?.filter(
                      (a) => a.status === 'assigned'
                    ).length || 0
                  } of ${
                    bulkProgress.assignments?.length || 0
                  } judges assigned`}
              </div>
            </div>

            {/* Content */}
            <div
              style={{
                padding: '12px 24px',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {bulkProgress.assignments &&
                bulkProgress.assignments.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr 50px 90px',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom:
                        i < bulkProgress.assignments.length - 1
                          ? '1px solid #f5f5f5'
                          : 'none',
                    }}
                  >
                    {/* Status indicator - fixed 18px for all states */}
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {(a.status === 'pending' ||
                        a.status === 'searching' ||
                        a.status === 'assigning') && (
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #e5e5e5',
                            borderTopColor: '#00a2ff',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                          }}
                        />
                      )}
                      {(a.status === 'found' || a.status === 'assigned') && (
                        <div
                          style={{
                            width: '18px',
                            height: '18px',
                            background: '#00a2ff',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontSize: '11px',
                          }}
                        >
                          ✓
                        </div>
                      )}
                      {(a.status === 'not_found' || a.status === 'error') && (
                        <div
                          style={{
                            width: '18px',
                            height: '18px',
                            background: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#999',
                            fontSize: '11px',
                          }}
                        >
                          ?
                        </div>
                      )}
                    </div>

                    {/* Name */}
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: '14px',
                          color:
                            a.status === 'not_found' || a.status === 'error'
                              ? '#999'
                              : '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {a.name}
                        {/* Show matched name if different from input (fuzzy match) */}
                        {a.matchedName &&
                          a.matchedName.toLowerCase() !==
                            a.name.toLowerCase() &&
                          (a.status === 'found' || a.status === 'assigned') && (
                            <span
                              style={{
                                color: '#f57c00',
                                marginLeft: '6px',
                                fontSize: '12px',
                              }}
                            >
                              → {a.matchedName}
                            </span>
                          )}
                      </div>
                      {/* Search input for not found - only in resolution phase */}
                      {a.status === 'not_found' &&
                        bulkProgress.phase === 'needs_resolution' && (
                          <div style={{ marginTop: '4px' }}>
                            <JudgeSearchInput
                              gender={gender}
                              placeholder={
                                a.candidates?.length > 0
                                  ? `Try: ${a.candidates[0]?.name}`
                                  : 'Search for judge...'
                              }
                              onSelect={(judge) =>
                                handleSelectAlternative(a.name, judge)
                              }
                            />
                          </div>
                        )}
                      {/* Search input for found matches - allow user to change in resolution phase */}
                      {a.status === 'found' &&
                        bulkProgress.phase === 'needs_resolution' && (
                          <div style={{ marginTop: '4px' }}>
                            <JudgeSearchInput
                              gender={gender}
                              placeholder={
                                a.isExactMatch
                                  ? 'Search to change...'
                                  : 'Search for different judge...'
                              }
                              onSelect={(judge) =>
                                handleSelectAlternative(a.name, judge)
                              }
                            />
                          </div>
                        )}
                    </div>

                    {/* Event - aligned column */}
                    <div
                      style={{
                        fontFamily: 'ui-monospace, monospace',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#333',
                        textAlign: 'center',
                      }}
                    >
                      {a.apparatus}
                      {a.judgeIndex + 1}
                    </div>

                    {/* Status text */}
                    <div
                      style={{
                        fontSize: '12px',
                        textAlign: 'right',
                        color:
                          a.status === 'assigned'
                            ? '#2e7d32'
                            : a.status === 'found' && a.isExactMatch
                            ? '#1565c0'
                            : a.status === 'found' && !a.isExactMatch
                            ? '#f57c00'
                            : a.status === 'not_found' || a.status === 'error'
                            ? '#c62828'
                            : '#888',
                      }}
                    >
                      {a.status === 'pending' && '—'}
                      {a.status === 'searching' && 'Searching...'}
                      {a.status === 'found' && a.isExactMatch && 'Found'}
                      {a.status === 'found' && !a.isExactMatch && 'Fuzzy Match'}
                      {a.status === 'not_found' && 'Not Found'}
                      {a.status === 'assigning' && 'Assigning...'}
                      {a.status === 'assigned' && 'Assigned'}
                      {a.status === 'error' && 'Error'}
                    </div>
                  </div>
                ))}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: '16px 24px',
                borderTop: '1px solid #e5e5e5',
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
              }}
            >
              {bulkProgress.phase === 'needs_resolution' ? (
                <>
                  <button
                    onClick={() => {
                      setAutoAssignCountdown(null);
                      setBulkProgress(null);
                      setParsedAssignments(null);
                      setJudgeOverrides({});
                    }}
                    style={{
                      padding: '10px 32px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#333',
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseOver={(e) => (e.target.style.background = '#f5f5f5')}
                    onMouseOut={(e) => (e.target.style.background = '#fff')}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleContinueAssignment}
                    style={{
                      padding: '10px 32px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#fff',
                      background: '#00a2ff',
                      border: 'none',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      minWidth: '120px',
                    }}
                    onMouseOver={(e) => (e.target.style.background = '#0088dd')}
                    onMouseOut={(e) => (e.target.style.background = '#00a2ff')}
                  >
                    {autoAssignCountdown !== null
                      ? `Continue (${autoAssignCountdown})`
                      : 'Continue'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setBulkProgress(null)}
                  disabled={bulkProgress.phase === 'done' && !canCloseModal}
                  style={{
                    padding: '10px 48px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color:
                      bulkProgress.phase === 'done' && !canCloseModal
                        ? '#999'
                        : '#333',
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '20px',
                    cursor:
                      bulkProgress.phase === 'done' && !canCloseModal
                        ? 'not-allowed'
                        : 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseOver={(e) => {
                    if (!(bulkProgress.phase === 'done' && !canCloseModal)) {
                      e.target.style.background = '#f5f5f5';
                    }
                  }}
                  onMouseOut={(e) => (e.target.style.background = '#fff')}
                >
                  {bulkProgress.phase === 'done' && !canCloseModal
                    ? 'Please wait...'
                    : 'Close'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
      {apparatusArray.map((a, i) => (
        <Row className={`${row} ${apparatusRow}`} key={i}>
          <Col className={squadLeft} xs={9}>
            <div className={flexTableWrapper}>
              <ScoreSheetLineup
                squad={team?.events?.[i]?.lineup}
                showSidebar={true}
                apparatus={a}
                max={lineupMaxes?.[i]}
                isIndividuals={team?.individuals}
                teamsByAthleteId={teamsByAthleteId}
              />
            </div>
            <ScoreSheetRounds
              data={team?.individuals ? {} : team?.events?.[i]}
              apparatus={a}
            />
          </Col>
          <Col className={squadRight} xs={3}>
            <div className={flexTableWrapper}>
              <ScoreSheetJudgeLineup
                judges={judgesByApparatus[a]}
                showSidebar={false}
                apparatus={a}
                max={lineupMaxes?.[i]}
                gender={gender}
                editable={editable}
                session={session}
                onJudgeAssign={handleJudgeAssign}
                onJudgeRemove={handleJudgeRemove}
                onBulkJudgeAssign={handleBulkJudgeAssign}
              />
            </div>
            <div className={rotationScore}>&nbsp;</div>
          </Col>
        </Row>
      ))}
      <Row>
        <Col className={squadLeft} xs={9}>
          <Row className={teamScore}>
            <Col className={teamScoreLabel}>
              {!team?.individuals && !!team?.neutral ? 'Team ND: ' : null}
            </Col>
            <Col
              className={
                !team?.individuals && !!team?.neutral
                  ? teamScoreValue
                  : teamScoreValueEmpty
              }
            >
              {!team?.individuals && !!team?.neutral
                ? convertMillipointsToDisplay(team?.neutral, 1)
                : null}
            </Col>
            <Col className={teamScoreLabel}>Final Score: </Col>
            <Col className={teamScoreValue}>
              {!team?.individuals ? team?.finalScore : null}
            </Col>
          </Row>
        </Col>
        <Col className={squadRight} xs={3}></Col>
      </Row>
      <Row className={`${row}${team?.individuals ? ` ${apparatusRow}` : ''}`}>
        <Col className={squadFull}>
          <ScoreSheetAA
            data={team?.individuals && !team?.aa?.length ? [] : team?.aa}
            showSidebar={true}
            apparatus={apparatusArray}
            max={lineupMax}
          />
        </Col>
      </Row>
    </main>
  );
}

function WomensSingleFooter({
  team,
  session,
  page,
  timezone,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const coachName =
    coachData?.[session?.gender]?.[team?.rtnId]?.headCoach ??
    coachData['C1'].name;

  const teamDisplayName = team?.forceTitle
    ? `${team?.altName}: ${team?.displayName}`
    : team?.altName;

  const copyrightTimePageFooter = (page) => {
    return (
      <section className={subFooter}>
        <Row>
          <Col className={timestamp}>
            <span>
              © {new Date().getFullYear()} Virtius&nbsp;&nbsp;»&nbsp;&nbsp;
            </span>
            <Clock timezone={timezone} />
            <span>
              &nbsp;&nbsp;»&nbsp;&nbsp;Page {singleSheetOnly ? 1 : page} of{' '}
              {singleSheetOnly ? 1 : sheetOptionsLength}
            </span>
          </Col>
        </Row>
      </section>
    );
  };

  return (
    <section className={footer}>
      <section className={subFooter}>
        <Row className={row}>
          <Col className={signature}>
            <div className={signatureLabel}>{`Coach: `}</div>
            <div className={signatureField}></div>
          </Col>
          <Col className={yellowCard}>
            <Form.Check
              type="checkbox"
              label="Yellow Card"
              id="yellowCard"
              checked={!!team?.conductCard}
              readOnly
            />
          </Col>
        </Row>
        <Row className={row}>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>
            <div className={signatureName}>
              {coachName} ({teamDisplayName})
            </div>
          </Col>
          <Col className={signatureNames}>
            <div className={signatureLabel}></div>
          </Col>
        </Row>
      </section>
      {copyrightTimePageFooter(page)}
    </section>
  );
}

export const WomensSingleScoresheet = memo(_WomensSingleScoresheet);

function _WomensSingleScoresheet({
  options,
  page,
  teams,
  teamAIndex: customTeamAIndex,
  format,
  date,
  timezone,
  location,
  attendance,
  session,
  apparatusArray,
  lineupMax,
  teamsByAthleteId,
  judgesByApparatus,
  singleSheetOnly,
  sheetOptionsLength,
  editable = false,
  onRefetch,
  onLocationChange,
  onAttendanceChange,
}) {
  let aIndex = options && options.length > 0 ? parseInt(options) ?? null : null;

  // Override with custom settings
  if (customTeamAIndex !== null && customTeamAIndex !== undefined) {
    aIndex = customTeamAIndex;
  }

  const team = aIndex < teams.length ? teams[aIndex] : {};

  return (
    <>
      <WomensSingleHeader
        team={team}
        format={format}
        date={date}
        timezone={timezone}
        location={location}
        attendance={attendance}
        session={session}
        editable={editable}
        onLocationChange={onLocationChange}
        onAttendanceChange={onAttendanceChange}
      />
      <WomensSingleBody
        team={team}
        apparatusArray={apparatusArray}
        lineupMax={lineupMax}
        teamsByAthleteId={teamsByAthleteId}
        judgesByApparatus={judgesByApparatus}
        gender={session.gender}
        session={session}
        editable={editable}
        onRefetch={onRefetch}
      />
      <WomensSingleFooter
        team={team}
        session={session}
        page={page}
        timezone={timezone}
        singleSheetOnly={singleSheetOnly}
        sheetOptionsLength={sheetOptionsLength}
      />
    </>
  );
}
